import React, { useEffect, useState } from 'react';
import { MainLayout } from '../components/MainLayout';
import { Button } from '../components/Button';
import { getDevices, renameDevice, revokeDevice } from '../api/security';
import { useAuth } from '../contexts/AuthContext';
import { Smartphone, Monitor, PencilLine, ShieldCheck } from 'lucide-react';

export default function Devices() {
  const { user } = useAuth();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [draftNames, setDraftNames] = useState({});

  useEffect(() => {
    let cancelled = false;

    const loadDevices = async () => {
      try {
        const data = await getDevices();
        if (!cancelled) {
          setDevices(data);
          setDraftNames(data.reduce((accumulator, device) => {
            accumulator[device.id] = device.device_name || '';
            return accumulator;
          }, {}));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDevices();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleRename = async (id) => {
    setBusyId(id);
    try {
      const updated = await renameDevice(id, draftNames[id] || 'Unnamed Device');
      setDevices((current) => current.map((device) => (device.id === id ? updated : device)));
    } finally {
      setBusyId('');
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Revoke this device and its sessions?')) {
      return;
    }

    setBusyId(id);
    try {
      await revokeDevice(id);
      setDevices((current) => current.filter((device) => device.id !== id));
    } finally {
      setBusyId('');
    }
  };

  return (
    <MainLayout>
      <div className="page-header notes-hero-banner">
        <div className="notes-hero-content">
          <div>
            <h1 className="notes-hero-title">Devices</h1>
            <p className="notes-hero-subtitle">Review trusted and active devices for {user?.email}</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '240px', color: 'var(--text-secondary)', gap: '12px' }}>
          Loading devices...
        </div>
      ) : (
        <div className="item-grid">
          {devices.length === 0 ? (
            <div className="glass-panel" style={{ padding: '32px', color: 'var(--text-secondary)' }}>
              No devices found.
            </div>
          ) : devices.map((device) => (
            <div key={device.id} className="glass-panel vault-item" style={{ display: 'grid', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div style={{ background: 'rgba(59, 130, 246, 0.1)', padding: '12px', borderRadius: '12px', color: 'var(--accent-primary)' }}>
                    {String(device.device_name || '').toLowerCase().includes('mobile') ? <Smartphone size={24} /> : <Monitor size={24} />}
                  </div>
                  <div>
                    <div className="item-title">{device.device_name || 'Unknown device'}</div>
                    <div className="item-subtitle">{device.device_type || 'Unknown platform'} • {device.last_active_at ? new Date(device.last_active_at).toLocaleString() : 'Never active'}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {device.is_current ? (
                  <div className="chip success">
                    <ShieldCheck size={14} /> Current
                  </div>
                ) : null}
                {device.is_trusted ? (
                  <div className="chip success">
                    <ShieldCheck size={14} /> Trusted
                  </div>
                ) : (
                  <div className="chip warning">Untrusted</div>
                )}
                </div>
              </div>

              <input
                className="input-field"
                value={draftNames[device.id] || ''}
                onChange={(event) => setDraftNames((current) => ({ ...current, [device.id]: event.target.value }))}
                aria-label="Device name"
              />

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <Button onClick={() => handleRename(device.id)} loading={busyId === device.id}>
                  <PencilLine size={16} style={{ marginRight: '8px' }} />
                  Rename
                </Button>
                <Button variant="danger" onClick={() => handleRevoke(device.id)} loading={busyId === device.id}>
                  Revoke
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </MainLayout>
  );
}
